import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to format BOM quantity display (no conversion needed - everything is in native units)
const formatBOMQuantity = (qty, unit, itemType) => {
  return `${qty.toFixed(2)} ${unit}`;
};

const LogReturnModal = ({ isOpen, onClose, workOrder, component, onSuccess }) => {
  const [lotNumber, setLotNumber] = useState("");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("unused");
  const [loading, setLoading] = useState(false);
  const [pickedLots, setPickedLots] = useState([]);
  const [recentReturns, setRecentReturns] = useState([]);

  useEffect(() => {
    if (isOpen && component) {
      // Get lots that have picks
      const lots = component.lots?.filter(lot => lot.picks && lot.picks.length > 0) || [];
      setPickedLots(lots);

      // Get recent returns
      const returns = [];
      component.lots?.forEach(lot => {
        lot.returns?.forEach(ret => {
          returns.push({
            ...ret,
            lot_number: lot.lot_number
          });
        });
      });
      returns.sort((a, b) => new Date(b.returned_at) - new Date(a.returned_at));
      setRecentReturns(returns.slice(0, 5));
    }
  }, [isOpen, component]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!lotNumber || !quantity || parseFloat(quantity) <= 0) {
      toast.error("Please select lot number and enter valid quantity");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/production/${workOrder.wo_id}/log-return`, null, {
        params: {
          component_id: component.component_id,
          lot_number: lotNumber,
          quantity: parseFloat(quantity),
          returned_by: "production",
          reason: reason
        }
      });
      
      toast.success("Return logged successfully");
      setLotNumber("");
      setQuantity("");
      setReason("unused");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error logging return:", error);
      toast.error(error.response?.data?.detail || "Failed to log return");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5" />
            Log Return - {component?.component_name}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Lot Number Selection */}
          <div>
            <Label htmlFor="lot-number">Lot Number</Label>
            <select
              id="lot-number"
              value={lotNumber}
              onChange={(e) => setLotNumber(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mt-1"
              required
            >
              <option value="">Select lot to return from...</option>
              {pickedLots.map((lot, idx) => (
                <option key={idx} value={lot.lot_number}>
                  {lot.lot_number} (Picked: {formatBOMQuantity(lot.picks?.reduce((sum, p) => sum + p.picked_qty, 0), component?.unit, component?.item_type)})
                </option>
              ))}
            </select>
            {pickedLots.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                ⚠️ No lots have been picked yet. Pick inventory first.
              </p>
            )}
          </div>

          {/* Quantity Input */}
          <div>
            <Label htmlFor="quantity">Quantity ({component?.unit})</Label>
            <Input
              id="quantity"
              type="number"
              step="0.01"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Enter quantity to return"
              required
            />
          </div>

          {/* Reason Selection */}
          <div>
            <Label htmlFor="reason">Return Reason</Label>
            <select
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mt-1"
            >
              <option value="unused">Unused</option>
              <option value="overstock">Overstock</option>
              <option value="damaged">Damaged</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Recent Return History */}
          {recentReturns.length > 0 && (
            <div>
              <Label className="mb-2 block">Recent Returns</Label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {recentReturns.map((ret, idx) => (
                  <Card key={idx} className="bg-gray-50">
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start text-sm">
                        <div>
                          <div className="font-mono font-semibold">{ret.lot_number}</div>
                          <div className="text-xs text-gray-600">
                            {formatBOMQuantity(ret.returned_qty, component?.unit, component?.item_type)} • {ret.reason} • {ret.returned_by}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(ret.returned_at).toLocaleString()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 justify-end pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || pickedLots.length === 0}>
              {loading ? "Logging..." : "Log Return"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LogReturnModal;
